import React, { useEffect, useMemo, useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import { createClerkSupabaseClient } from '../../../lib/supabase.js';
import { formatDateTime, formatNumber } from '../../../styles/tokens/formatters';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Input } from '../../../components/ui/input';
import { Search, ChevronDown, ChevronRight, ArrowUpDown } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import SectionHeader from '../../../components/ui/fancy/SectionHeader';
import DashboardFilters from '../../../components/ui/fancy/DashboardFilters';
import { KpiGrid } from '../../../components/ui/KpiCard';
import GlobalSearch from '../../../components/ui/fancy/GlobalSearch';
import { useNavigate } from 'react-router-dom';
import { subDays } from 'date-fns';

const safeParseJson = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return {}; }
};

const CertificadoRow = ({ certificado, expanded, onToggle }) => {
  const emailPayload = safeParseJson(certificado.email_payload);
  const multiples = Array.isArray(emailPayload?.datosCompletos?.multiples_certificados)
    ? emailPayload.datosCompletos.multiples_certificados
    : [];

  // Si hay duplicados por agrupación, contar esos también
  const certificadosDuplicados = certificado.certificados ? certificado.certificados.length : 0;
  const totalCertificados = Math.max(multiples.length, certificadosDuplicados);
  const esMultiple = totalCertificados > 1;

  const displayCertCount = esMultiple ? `${totalCertificados} certificados` : '1 certificado';

  return (
    <>
      <TableRow
        className="cursor-pointer border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors"
        onClick={() => onToggle(certificado.id)}
      >
        <TableCell className="w-6 text-gray-400">
          {expanded
            ? <ChevronDown className="h-4 w-4" />
            : <ChevronRight className="h-4 w-4" />}
        </TableCell>
        <TableCell className="text-sm font-mono font-semibold text-gray-900 dark:text-white py-4 px-4">
          {certificado.subscription_codigo || 'N/A'}
        </TableCell>
        <TableCell className="text-sm py-4 px-4">
          <div className="font-medium text-gray-900 dark:text-white">{certificado.compania_nombre || 'N/A'}</div>
          <div className="text-xs text-gray-500 dark:text-gray-500 font-mono">{certificado.compania_rut || 'N/A'}</div>
        </TableCell>
        <TableCell className="text-sm py-4 px-4">
          {esMultiple ? (
            <>
              <div className="font-medium text-gray-900 dark:text-white">{displayCertCount}</div>
              <div className="text-xs text-gray-500 dark:text-gray-500">múltiples productos</div>
            </>
          ) : (
            <>
              <div className="font-medium text-gray-900 dark:text-white">{certificado.producto_nombre || 'N/A'}</div>
              <div className="text-xs text-gray-500 dark:text-gray-500">{certificado.plan_nombre || 'N/A'}</div>
            </>
          )}
        </TableCell>
        <TableCell className="text-sm py-4 px-4 text-center">
          <span className="text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded px-2.5 py-1">
            {certificado.tipo_certificado || 'N/A'}
          </span>
        </TableCell>
        <TableCell className="text-sm text-right py-4 px-4">
          <div className="font-semibold text-gray-900 dark:text-white">
            {certificado.monto == null ? 'N/A' : `${formatNumber(certificado.monto)} UF`}
          </div>
        </TableCell>
        <TableCell className="text-sm py-4 px-4">
          <span className={`text-xs font-medium ${
            certificado.subscription_status === 'confirmada' || certificado.subscription_status === 'activa'
              ? 'text-[var(--yool-success)] dark:text-green-400'
              : certificado.subscription_status === 'pendiente' || certificado.subscription_status === 'suscribiendo'
              ? 'text-yellow-600 dark:text-yellow-400'
              : 'text-gray-600 dark:text-gray-400'
          }`}>
            {certificado.subscription_status || 'N/A'}
          </span>
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow className="cursor-default">
          <TableCell colSpan={8} className="bg-gray-50 dark:bg-gray-800/30 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="space-y-4">
              <div className="bg-white dark:bg-gray-900/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Cliente</span>
                    <div className="font-medium text-gray-900 dark:text-white">{certificado.cliente_nombre || 'N/A'}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 font-mono">{certificado.cliente_rut || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Fecha Emisión</span>
                    <div className="text-sm text-gray-900 dark:text-white">{formatDateTime(certificado.fecha_emision)}</div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Inicio Cobertura</span>
                    <div className="text-sm text-gray-900 dark:text-white">{certificado.fecha_inicio_cobertura || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Fin Cobertura</span>
                    <div className="text-sm text-gray-900 dark:text-white">{certificado.fecha_termino_cobertura || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {esMultiple && (
                <div className="bg-white dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Certificados incluidos</h4>
                  </div>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {multiples.length > 0 ? (
                      multiples.map((cert, idx) => (
                        <div key={idx} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Código</span>
                              <div className="font-mono text-sm font-semibold text-gray-900 dark:text-white">{cert.codigo || 'N/A'}</div>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Producto</span>
                              <div className="text-sm text-gray-900 dark:text-white">{cert.producto || 'N/A'}</div>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Plan</span>
                              <div className="text-sm text-gray-900 dark:text-white">{cert.plan || 'N/A'}</div>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Prima (UF)</span>
                              <div className="text-sm font-semibold text-gray-900 dark:text-white">{formatNumber(cert.prima_uf)}</div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      certificado.certificados?.map((cert, idx) => (
                        <div key={idx} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Código</span>
                              <div className="font-mono text-sm font-semibold text-gray-900 dark:text-white">{cert.codigo || 'N/A'}</div>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Producto</span>
                              <div className="text-sm text-gray-900 dark:text-white">{cert.producto_nombre || 'N/A'}</div>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Plan</span>
                              <div className="text-sm text-gray-900 dark:text-white">{cert.plan_nombre || 'N/A'}</div>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Prima (UF)</span>
                              <div className="text-sm font-semibold text-gray-900 dark:text-white">{formatNumber(cert.monto)}</div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const ConversionesCertificados = () => {
  const { session } = useSession();
  const supabase = useMemo(() => createClerkSupabaseClient(session), [session]);
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [growthData, setGrowthData] = useState({ total: 0, lastPeriod: 0, percent: 0 });
  const [last30DaysCount, setLast30DaysCount] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  useEffect(() => {
    if (!session) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const now = new Date();
        const from = filters.dateRange?.from ?? subDays(now, 30);
        const to = filters.dateRange?.to ?? now;

        const diff = to.getTime() - from.getTime();
        const prevFrom = new Date(from.getTime() - diff);
        const prevTo = new Date(from.getTime() - 1);

        const [currentRes, prevRes, listRes] = await Promise.all([
          supabase.from('company_certificados')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', from.toISOString())
            .lte('created_at', to.toISOString()),
          supabase.from('company_certificados')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', prevFrom.toISOString())
            .lte('created_at', prevTo.toISOString()),
          supabase.from('company_certificados')
            .select('*')
            .gte('created_at', from.toISOString())
            .lte('created_at', to.toISOString())
            .order('subscription_codigo', { ascending: true })
            .order('created_at', { ascending: false })
            .limit(500),
        ]);

        if (listRes.error) throw listRes.error;

        const currentCount = currentRes.count || 0;
        const prevCount = prevRes.count || 0;
        setLast30DaysCount(currentCount);
        setGrowthData({
          total: currentCount,
          lastPeriod: prevCount,
          percent: prevCount > 0 ? ((currentCount - prevCount) / prevCount) * 100 : 0,
        });

        // Agrupar por subscription_codigo para evitar duplicados
        const grouped = {};
        const dataList = Array.isArray(listRes.data) ? listRes.data : [];

        dataList.forEach((row) => {
          const key = row.subscription_codigo || row.id;
          if (!grouped[key]) {
            grouped[key] = { ...row, certificados: [row] };
          } else {
            grouped[key].certificados.push(row);
          }
        });

        // Ordenar por fecha de creación (descendente = más reciente primero)
        const sortedRows = Object.values(grouped).sort((a, b) => {
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return dateB - dateA; // Descendente: recientes primero
        });

        setRows(sortedRows);
      } catch (e) {
        setError(e?.message || 'Error cargando datos');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [session, supabase, filters]);

  const filteredRows = useMemo(() => {
    let filtered = [...rows];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((r) =>
        r.subscription_codigo?.toLowerCase().includes(q) ||
        r.codigo?.toLowerCase().includes(q) ||
        r.compania_nombre?.toLowerCase().includes(q) ||
        r.producto_nombre?.toLowerCase().includes(q) ||
        r.cliente_nombre?.toLowerCase().includes(q) ||
        r.cliente_rut?.toLowerCase().includes(q)
      );
    }

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        let comparison = 0;
        if (typeof aValue === 'string') {
          comparison = aValue.localeCompare(bValue || '');
        } else if (typeof aValue === 'number') {
          comparison = aValue - bValue;
        } else {
          comparison = String(aValue).localeCompare(String(bValue));
        }

        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    return filtered;
  }, [rows, searchQuery, sortConfig]);

  const handleToggleRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const kpiItems = [
    {
      title: 'Certificados en rango',
      value: last30DaysCount,
      helper: 'Total de certificados',
      trend: 'neutral',
    },
    {
      title: 'Período anterior',
      value: growthData.lastPeriod,
      helper: 'Comparación equivalente',
      trend: growthData.percent >= 0 ? 'up' : 'down',
      delta: `${growthData.percent >= 0 ? '+' : ''}${Math.abs(growthData.percent).toFixed(1)}% vs período actual`,
    },
  ];

  return (
    <div className="space-y-8">
      <SectionHeader
        title="Conversiones - Certificados"
        subtitle="Agrupados por suscripción"
        action={<GlobalSearch onNavigate={(p) => navigate(p)} />}
      />

      <DashboardFilters
        onFilterChange={setFilters}
        showClientFilter={false}
        channels={[
          { value: 'web', label: 'Web Directa' },
          { value: 'fb', label: 'Facebook Ads' },
        ]}
      />

      <KpiGrid items={kpiItems} loading={loading} />

      <Card className="border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm">
        <CardHeader className="flex flex-col md:flex-row items-center justify-between gap-4 border-b border-gray-200 dark:border-gray-700 pb-4">
          <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">Certificados</CardTitle>
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar suscripción, código o cliente..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 py-2 border border-gray-300 dark:border-gray-600 text-sm"
            />
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {loading ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">Cargando certificados...</div>
          ) : error ? (
            <div className="text-sm text-[var(--yool-error)] dark:text-red-400 py-8 text-center">{error}</div>
          ) : filteredRows.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">No hay certificados para mostrar</div>
          ) : (
            <div className="relative w-full overflow-auto">
              <Table>
                <TableHeader className="bg-gray-100 dark:bg-gray-800 border-y border-gray-300 dark:border-gray-700">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-6 text-gray-700 dark:text-gray-300 font-semibold text-xs h-12 px-4" />
                    <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-12 px-4">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort('subscription_codigo')}
                        className="-ml-4 h-8 data-[state=open]:bg-accent"
                      >
                        Suscripción
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-12 px-4">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort('compania_nombre')}
                        className="-ml-4 h-8 data-[state=open]:bg-accent"
                      >
                        Compañía
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-12 px-4">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort('producto_nombre')}
                        className="-ml-4 h-8 data-[state=open]:bg-accent"
                      >
                        Producto / Plan
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-center text-gray-700 dark:text-gray-300 font-semibold text-xs h-12 px-4">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort('tipo_certificado')}
                        className="-ml-4 h-8 data-[state=open]:bg-accent"
                      >
                        Tipo
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-right text-gray-700 dark:text-gray-300 font-semibold text-xs h-12 px-4">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort('monto')}
                        className="-ml-4 h-8 data-[state=open]:bg-accent"
                      >
                        Prima Total
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-12 px-4">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort('subscription_status')}
                        className="-ml-4 h-8 data-[state=open]:bg-accent"
                      >
                        Estado Suscripción
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.map((certificado) => (
                    <CertificadoRow
                      key={certificado.id}
                      certificado={certificado}
                      expanded={expandedRows[certificado.id] || false}
                      onToggle={handleToggleRow}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConversionesCertificados;
